import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Params } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { ContainerComponent } from '@shared/components/container/container.component';
import { DataFieldComponent } from '@shared/components/data-field/data-field.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { AutofillComponent } from '@shared/components/form/autofill/autofill.component';
import { SelectDateComponent } from '@shared/components/form/select-date/select-date.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { MovementSelectionComponent } from '@shared/components/movement-selection/movement-selection.component';
import { BodyBlockComponent } from '@shared/components/pdf-viewer/body-block/body-block.component';
import { PdfSubtitleBlockComponent } from '@shared/components/pdf-viewer/subtitle-block/subtitle-block.component';
import { RowComponent } from '@shared/components/row/row.component';
import { SelectionButtonComponent } from '@shared/components/selection-button/selection-button.component';
import { SubtitleBlockComponent } from '@shared/components/subtitle-block/subtitle-block.component';
import { VesselItinerary } from '@shared/types/shared.types';
import { ItineraryState } from '@vessel-itinerary/state/itinerary.state';
import { StepProgress, StepProgressComponent } from '@vessel-reports/noon/components/step-progress/step-progress.component';
import { SupplyDetailsComponent } from '@vessel-reports/port/components/supply-details/supply-details.component';
import {
  ArrivalSteps,
  Consumptions,
  DepatureSteps,
  PortInformationArrival,
  PortInformationDeparture,
  REPORT_STATUS,
  REPORT_TYPE,
  VesselTypes
} from '@vessel-reports/port/port.constants';
import { PortActions } from '@vessel-reports/port/state/port.action';
import { PortState } from '@vessel-reports/port/state/port.state';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import { VesselReportsState } from '@vessel-reports/state/vessel-reports.state';
import { Movement, MovementDetail } from '@vessel-reports/state/vessel-reports.types';
import { DatetimeLocalPipe } from 'app/core/pipe/datetime/datetime-local.pipe';
import { MathRoundPipe } from 'app/core/pipe/math-round.pipe';
import dayjs from 'dayjs';
import { find, isEmpty } from 'lodash';
import { map } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import { Subject, take, takeUntil } from 'rxjs';
import { Bunker, Button, InputROB, PortReportDetail, ReportItem, Step } from '../../state/port.types';
import { InputInventoryRobComponent } from '../input-inventory-rob/input-inventory-rob.component';

@Component({
  selector: 'port-report-details',
  imports: [
    TranslocoPipe,
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    StepProgressComponent,
    SubtitleBlockComponent,
    TextboxComponent,
    AutofillComponent,
    SelectDateComponent,
    FooterComponent,
    SelectionButtonComponent,
    InputInventoryRobComponent,
    SupplyDetailsComponent,
    PdfSubtitleBlockComponent,
    BodyBlockComponent,
    MathRoundPipe,
    DataFieldComponent,
    ContainerComponent,
    MatIcon,
    DatetimeLocalPipe,
    RowComponent,
    MovementSelectionComponent
  ],
  templateUrl: './port-report-details.component.html',
  styleUrl: './port-report-details.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatetimeLocalPipe]
})
export class PortReportDetailsComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly popupService: PopupService = inject(PopupService);
  protected readonly datetimeLocalPipe: DatetimeLocalPipe = inject(DatetimeLocalPipe);
  protected readonly profileState: ProfileState = inject(ProfileState);
  arrivalSteps: WritableSignal<StepProgress[]> = signal(cloneDeep(ArrivalSteps));
  departureSteps: WritableSignal<StepProgress[]> = signal(cloneDeep(DepatureSteps));
  buttons: WritableSignal<Button[]> = signal(cloneDeep(VesselTypes));
  protected readonly REPORT_STATUS: typeof REPORT_STATUS = REPORT_STATUS;
  protected readonly isEmpty: any = isEmpty;
  type: WritableSignal<'arrival' | 'departure'> = signal('arrival');
  mode: WritableSignal<
    'input-rob' | 'input-inventory' | 'details' | 'last-port-movements' | 'next-port-movements' | 'departure-port-movements' | 'arrival-port-movements'
  > = signal('details');
  currentArrivalStep: WritableSignal<Step> = signal(1); // 5: review
  currentDepartureStep: WritableSignal<Step> = signal(1); // 3: review
  form: UntypedFormGroup;
  portInformationForm: UntypedFormGroup;
  consumptionForm: UntypedFormGroup;
  detailsForm: UntypedFormGroup;
  detailPortReport: Signal<PortReportDetail> = computed(() => this._portReportState.detailPortReport());
  previousNoonReport: Signal<PortReportDetail> = computed(() => this._portReportState.previousReport());
  vesselMovements: WritableSignal<VesselItinerary[]> = signal([]);
  detailBunker: Signal<Bunker> = computed(() => this._portReportState.detailBunker());
  movements: Signal<Movement> = computed(() => this._vesselReportStates.pagination().movements);
  private readonly _portReportState: PortState = inject(PortState);
  private readonly _itineraryState: ItineraryState = inject(ItineraryState);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _portReportActions: PortActions = inject(PortActions);
  private readonly _vesselReportActions: VesselReportsAction = inject(VesselReportsAction);
  private readonly _vesselReportStates: VesselReportsState = inject(VesselReportsState);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _injector: Injector = inject(Injector);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  queryParams: WritableSignal<Params> = signal(void 0);

  constructor() {
    this.useEffect();
  }

  ngOnInit() {
    try {
      this.type.set(this.detailPortReport()?.report_type_id === REPORT_TYPE.ARRIVAL ? 'arrival' : 'departure');
      if (this.detailPortReport() && this.detailPortReport()?.bunker_id) {
        this._portReportActions.actions.getBunker({
          bunker_id: this.detailPortReport()?.bunker_id
        });
      }
      this.form = this._formBuilder.group({
        rob_hfo: [this.detailBunker()?.rob_hfo, Validators.required],
        rob_mgo: [this.detailBunker()?.rob_mgo, Validators.required],
        rob_freshwater: [this.detailBunker()?.rob_freshwater, Validators.required],
        rob_luboil_me_cyl: [this.detailBunker()?.rob_luboil_me_cyl, Validators.required],
        rob_luboil_me_sys: [this.detailBunker()?.rob_luboil_me_sys, Validators.required],
        rob_luboil_ge_sys: [this.detailBunker()?.rob_luboil_ge_sys, Validators.required],
        vessel_report_id: [this.detailPortReport()?.id, Validators.required],
        date_report: [this.detailPortReport()?.date_report]
      });
      const portInformation = this.type() === 'arrival' ? PortInformationArrival : PortInformationDeparture;
      this.portInformationForm = this.createDetailsFormGroup(portInformation);
      this.consumptionForm = this.createDetailsFormGroup(Consumptions);
      this.detailsForm = this._formBuilder.group({
        vessel_report_id: [this.detailPortReport()?.id],
        details: this._formBuilder.array([])
      });

      // Update step
      if (this.detailPortReport()?.status_id === REPORT_STATUS.SUBMITTED || this.detailPortReport()?.status_id === REPORT_STATUS.SAVED) {
        this.currentArrivalStep.set(4);
        this.currentDepartureStep.set(4);
      }
      this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
        this.queryParams.set({ ...queryParams });
      });
      this.updateDetailValues(this.portInformationForm, this.detailPortReport()?.details);
      this.updateDetailValues(this.consumptionForm, this.detailPortReport()?.details);
      this.populateData();
      this.updateButtonStatus();
      this.updateStepProgress();
    } catch (error) {
      console.log('error', error);
    }
  }

  populateData() {
    try {
      if (this.type() === 'arrival') {
        !this.getControlByReportTypeId(this.portInformationForm, 38).value &&
          this.getControlByReportTypeId(this.portInformationForm, 38).setValue(this.movements()?.current_movement_id);
        !this.getControlByReportTypeId(this.portInformationForm, 43).value &&
          this.getControlByReportTypeId(this.portInformationForm, 43).setValue(this.movements()?.current_datetime_arrival);
        !this.getControlByReportTypeId(this.portInformationForm, 2).value &&
          this.getControlByReportTypeId(this.portInformationForm, 2).setValue(this.movements()?.previous_movement_id);
      } else {
        !this.getControlByReportTypeId(this.portInformationForm, 1).value &&
          this.getControlByReportTypeId(this.portInformationForm, 1).setValue(this.movements()?.next_movement_id);
        !this.getControlByReportTypeId(this.portInformationForm, 6).value &&
          this.getControlByReportTypeId(this.portInformationForm, 6).setValue(this.movements()?.next_datetime_arrival);
        !this.getControlByReportTypeId(this.portInformationForm, 38).value &&
          this.getControlByReportTypeId(this.portInformationForm, 38).setValue(this.movements()?.current_movement_id);
        !this.getControlByReportTypeId(this.portInformationForm, 48).value &&
          this.getControlByReportTypeId(this.portInformationForm, 48).setValue(this.movements()?.current_datetime_departure);
      }
    } catch (e) {
      console.log('e', e);
    }
  }

  useEffect() {
    effect(
      () => {
        if (this.detailBunker()) {
          this.form.get('rob_hfo').setValue(this.detailBunker().rob_hfo);
          this.form.get('rob_mgo').setValue(this.detailBunker().rob_mgo);
          this.form.get('rob_freshwater').setValue(this.detailBunker().rob_freshwater);
          this.form.get('rob_luboil_me_cyl').setValue(this.detailBunker().rob_luboil_me_cyl);
          this.form.get('rob_luboil_me_sys').setValue(this.detailBunker().rob_luboil_me_sys);
          this.form.get('rob_luboil_ge_sys').setValue(this.detailBunker().rob_luboil_ge_sys);
        }
      },
      { injector: this._injector }
    );
  }

  createDetailsFormGroup(data: ReportItem[]): FormGroup {
    return this._formBuilder.group({
      details: this._formBuilder.array(
        data.map((info) =>
          this._formBuilder.group({
            id: [info.id],
            report_item_id: [info.report_item_id],
            value: [info.value, !this.hasNoRequired(info.report_item_id) && Validators.required]
          })
        )
      )
    });
  }

  hasNoRequired(id: number) {
    return id === 9 || id === 40 || id === 41 || id === 42 || id === 45 || id === 46 || id === 47;
  }

  updateButtonStatus() {
    if (this.detailPortReport()) {
      const newButtons = map(this.buttons(), (button: Button) => {
        if (button.value === this.getDetailValueByReportTypeId(9)) {
          button.active = true;
        }
        return button;
      });
      this.buttons.set([...newButtons]);
    }
  }

  updateDetailValues(form: FormGroup, data: any[]) {
    const array = form.get('details') as FormArray;

    data?.forEach((newItem) => {
      const match = array.controls.find((ctrl) => ctrl.get('report_item_id')?.value === newItem.report_item_id);
      if (match) {
        if (
          newItem.report_item_id == 43 ||
          newItem.report_item_id == 40 ||
          newItem.report_item_id == 41 ||
          newItem.report_item_id == 42 ||
          newItem.report_item_id == 39 ||
          newItem.report_item_id == 46 ||
          newItem.report_item_id == 45 ||
          newItem.report_item_id == 47 ||
          newItem.report_item_id == 48 ||
          newItem.report_item_id == 6 ||
          newItem.report_item_id == 9
        ) {
          return match.patchValue({ value: newItem.value ? Number(newItem.value) : '', id: newItem.id });
        }
        match.patchValue({ value: newItem.value, id: newItem.id });
      }
    });
  }

  isSubmitStatus() {
    return this.detailPortReport()?.status_id === REPORT_STATUS.SUBMITTED;
  }

  isDisableSaveButton() {
    if (this.detailPortReport()?.report_type_id === REPORT_TYPE.ARRIVAL) {
      return this.currentArrivalStep() === 1
        ? this.portInformationForm.invalid
        : this.currentArrivalStep() === 2
          ? this.form.invalid
          : this.currentArrivalStep() === 3
            ? this.consumptionForm.invalid
            : false;
    }
    return this.currentDepartureStep() === 1
      ? this.portInformationForm.invalid
      : this.currentDepartureStep() === 2
        ? this.form.invalid
        : this.currentDepartureStep() === 3
          ? this.consumptionForm.invalid
          : false;
  }

  ngOnDestroy(): void {
    this._portReportActions.actions.cleanState();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  updateStepProgress() {
    if (this.type() === 'arrival' && this.currentArrivalStep() !== 4) {
      const steps = this.arrivalSteps();
      steps.forEach((step) => {
        step.active = false;
      });
      steps[this.currentArrivalStep() - 1].active = true;
      this.arrivalSteps.set([...steps]);
    }
    if (this.type() === 'departure' && this.currentDepartureStep() !== 4) {
      const steps = this.departureSteps();
      steps.forEach((step) => {
        step.active = false;
      });
      steps[this.currentDepartureStep() - 1].active = true;
      this.departureSteps.set([...steps]);
    }
  }

  get title() {
    if (isEmpty(this.detailPortReport())) {
      return '';
    }
    let title = '';
    title =
      dayjs(this.detailPortReport()?.date_report * 1000).format('DD MMM YYYY') +
      ` ${this.detailPortReport()?.report_type_id === REPORT_TYPE.ARRIVAL ? 'Arrival' : 'Departure'} Report`;
    return title;
  }

  get sizeBox() {
    if ((this.type() === 'arrival' && this.currentArrivalStep() !== 4) || (this.type() === 'departure' && this.currentDepartureStep() !== 4)) {
      return 'md';
    }
    return 'lg';
  }

  get saveTitle(): string {
    if (
      (this.detailPortReport().status_id === REPORT_STATUS.SAVED && this.currentArrivalStep() !== 4 && this.type() === 'arrival') ||
      (this.detailPortReport().status_id === REPORT_STATUS.SAVED && this.currentDepartureStep() !== 4 && this.type() === 'departure')
    ) {
      return 'Save';
    }
    let title = 'Next';
    if (this.type() === 'arrival') {
      if (this.currentArrivalStep() === 3) {
        title = 'Save & Review';
      } else if (this.currentArrivalStep() === 4) {
        title = 'Submit';
      }
    } else {
      if (this.currentDepartureStep() === 3) {
        title = 'Save & Review';
      } else if (this.currentDepartureStep() === 4) {
        title = 'Submit';
      }
    }
    if (this.detailPortReport()?.status_id === REPORT_STATUS.SUBMITTED) {
      return 'Revert Status to Saved';
    }
    return title;
  }

  getControlByReportTypeId(control: UntypedFormGroup, reportTypeId: number): FormControl {
    const array = control.get('details') as FormArray;

    const group = array.controls.find((g) => g.get('report_item_id')?.value === reportTypeId) as FormGroup;

    return group ? (group.get('value') as FormControl) : null;
  }

  getDetailValueByReportTypeId(reportTypeId: number, valueMovementType?: string): any {
    if (!this.detailPortReport()) {
      return '';
    }

    const reportItem = this.detailPortReport()?.details?.find((d) => d?.report_item_id === reportTypeId);
    if (reportTypeId === 1 || reportTypeId === 2 || reportTypeId === 38) {
      return find(this.vesselMovements(), (movementDetail: MovementDetail) => movementDetail?.id == reportItem?.value)?.[valueMovementType || 'port_name'];
    }
    if (
      reportTypeId == 43 ||
      reportTypeId == 40 ||
      reportTypeId == 41 ||
      reportTypeId == 42 ||
      reportTypeId == 39 ||
      reportTypeId == 46 ||
      reportTypeId == 45 ||
      reportTypeId == 47 ||
      reportTypeId == 48 ||
      reportTypeId == 9 ||
      reportTypeId == 6
    ) {
      return reportItem?.value ? Number(reportItem?.value) : '';
    }

    return reportItem?.value || '';
  }

  convertFormToFormData(form: FormGroup): FormData {
    const formData = new FormData();
    formData.append('vessel_report_id', form.get('vessel_report_id')?.value);

    const detailsArray = form.get('details') as FormArray;

    detailsArray.controls.forEach((group: AbstractControl, index: number) => {
      const groupValue = group.value;
      for (const key in groupValue) {
        if (Object.prototype.hasOwnProperty.call(groupValue, key)) {
          groupValue[key] && formData.append(`details[${index}][${key}]`, groupValue[key]);
        }
      }
    });

    return formData;
  }

  onDiscard: FunctionType = () => {
    this.onClose();
  };

  onSave: FunctionType = () => {
    try {
      if (this.detailPortReport().status_id === 3) {
        this._portReportActions.actions.updateReport({
          report_id: this.detailPortReport().id,
          body: {
            status_id: 2,
            date_report: this.detailPortReport().date_report
          }
        });
        this._portReportActions.actions.updateReportSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.getDetailReportWithCallback(() => {
            this._portReportActions.actions.getBunker({
              bunker_id: this.detailPortReport()?.bunker_id
            });
            this.getVesselsReport('6,8', this.queryParams());
          });
        });
        return;
      }

      if (this.type() === 'arrival') {
        const form = this.currentArrivalStep() === 1 ? this.portInformationForm : this.consumptionForm;
        const status = this.currentArrivalStep() === 1 ? REPORT_STATUS.DRAFT : this.currentArrivalStep() === 4 ? REPORT_STATUS.SUBMITTED : REPORT_STATUS.SAVED;

        if (this.currentArrivalStep() === 2) {
          this._portReportActions.actions.createReportBunker({ body: this.form.getRawValue() });
          this._portReportActions.actions.createReportBunkerSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.getDetailReportWithCallback(() => {
              if (this.detailPortReport().status_id === REPORT_STATUS.SAVED && this.currentArrivalStep() !== 4) {
                return this.currentArrivalStep.set(4);
              }
              this.currentArrivalStep.set(3);
              this.updateStepProgress();
            });
          });
          return;
        }
        this.handleUpdateDetailsReport(form, status, () => {
          if (this.detailPortReport().status_id === REPORT_STATUS.SAVED && this.currentArrivalStep() !== 4) {
            return this.currentArrivalStep.set(4);
          }
          if (this.currentArrivalStep() === 1) {
            this.currentArrivalStep.set(2);
          } else if (this.currentArrivalStep() === 3) {
            this.currentArrivalStep.set(4);
          } else {
            this.onClose();
          }
          this.updateStepProgress();
        });
      } else {
        const form = this.currentDepartureStep() === 1 ? this.portInformationForm : this.consumptionForm;
        const status =
          this.currentDepartureStep() === 1 ? REPORT_STATUS.DRAFT : this.currentDepartureStep() === 4 ? REPORT_STATUS.SUBMITTED : REPORT_STATUS.SAVED;

        if (this.currentDepartureStep() === 2) {
          this._portReportActions.actions.createReportBunker({ body: this.form.getRawValue() });
          this._portReportActions.actions.createReportBunkerSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.getDetailReportWithCallback(() => {
              if (this.detailPortReport().status_id === REPORT_STATUS.SAVED && this.currentDepartureStep() !== 4) {
                return this.currentDepartureStep.set(4);
              }
              this.currentDepartureStep.set(3);
              this.updateStepProgress();
            });
          });
          return;
        }
        this.handleUpdateDetailsReport(form, status, () => {
          if (this.detailPortReport().status_id === REPORT_STATUS.SAVED && this.currentDepartureStep() !== 4) {
            return this.currentDepartureStep.set(4);
          }
          if (this.currentDepartureStep() === 1) {
            this.currentDepartureStep.set(2);
          } else if (this.currentDepartureStep() === 2) {
            this.currentDepartureStep.set(3);
          } else if (this.currentDepartureStep() === 3) {
            this.currentDepartureStep.set(4);
          } else {
            this.onClose();
          }
          this.updateStepProgress();
        });
      }
    } catch (e) {
      console.log('error', e);
    }
  };

  handleUpdateDetailsReport(form: any, status: REPORT_STATUS, cb?: () => void) {
    if (status === REPORT_STATUS.SUBMITTED) {
      const hgoROB = this.detailBunker()?.rob_hfo;
      const mgoROB = this.detailBunker()?.rob_mgo;
      const eta: string = this.datetimeLocalPipe.transform(this.getDetailValueByReportTypeId(43), { format: 'DD MMM YYYY HH:mm' });
      const etd: string = this.datetimeLocalPipe.transform(this.getDetailValueByReportTypeId(48), { format: 'DD MMM YYYY HH:mm' });
      const content =
        this.type() === 'arrival'
          ? `<div>The following data has been updated to vessel movement:</div>
            <div>ETA: ${eta}</div>
            <div>Arrival ROB - HFO: ${hgoROB} MT</div>
            <div>Arrival ROB - MGO: ${mgoROB} MT</div>`
          : `<div>The following data has been updated to vessel movement:</div>
            <div>ETD: ${etd}</div>
            <div>Departure ROB - HFO: ${hgoROB} MT</div>
            <div>Departure ROB - MGO: ${mgoROB} MT</div>`;
      this.popupService.openConfirmationDialog({
        title: 'Updated vessel itinerary',
        message: content,
        iconType: 'success',
        onCancel: () => {},
        onAccept: () => {
          this._portReportActions.actions.submitPortReport({ report_id: this.detailPortReport()?.id });
          this._portReportActions.actions.submitPortReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.getVesselsReport('6,8', this.queryParams());
            cb?.();
          });
        }
      });
    } else {
      this.detailsForm.get('details').reset();
      const formArray = this.detailsForm.get('details') as FormArray;
      const details = form?.get('details').value;
      details?.forEach((item) => {
        formArray.push(
          this._formBuilder.group({
            id: [item.id],
            report_item_id: [item.report_item_id],
            value: [item.report_item_id === 9 ? item.value.toString() : item.value]
          })
        );
      });
      const formData = this.convertFormToFormData(this.detailsForm);
      if (this.currentDepartureStep() == 3 || this.currentArrivalStep() === 3) {
        formData.append('status_id', String(REPORT_STATUS.SAVED));
      }
      this._portReportActions.actions.updateDetailPortReport({ body: formData });
      this._portReportActions.actions.updateDetailPortReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        if (this.currentDepartureStep() == 3 || this.currentArrivalStep() === 3) {
          this.getVesselsReport('6,8', this.queryParams());
        }
        this.getDetailReportWithCallback(cb);
      });
    }
  }

  onClose: FunctionType = () => {
    this.close.emit();
  };

  onPressSection(step: Step) {
    if (this.type() === 'arrival') {
      this.currentArrivalStep.set(step);
    } else {
      this.currentDepartureStep.set(step);
    }
    this.updateStepProgress();
  }

  onBack() {
    this.mode.set('details');
  }

  onSaveROB(rob: InputROB) {
    this.form.patchValue(rob);
    this.consumptionForm.get('details').enable();
    this.mode.set('details');
  }

  onSaveSupplyDetails(rob: InputROB) {
    console.log('onSaveSupplyDetails', rob);
  }

  onPressLastPort() {
    this.mode.set('last-port-movements');
  }

  onPressNextPort() {
    this.mode.set('next-port-movements');
  }

  onPressArrivalPort() {
    this.mode.set('arrival-port-movements');
  }

  onPressDeparturePort() {
    this.mode.set('departure-port-movements');
  }

  onSavePort(movement: Partial<VesselItinerary>, type?: number) {
    this.getControlByReportTypeId(this.portInformationForm, type).setValue(movement?.id);
    this.mode.set('details');
  }

  onButtonsChange(buttons: Button[]) {
    const activeButton = buttons.find((button) => button.active);
    if (activeButton) {
      this.getControlByReportTypeId(this.portInformationForm, 9).setValue(activeButton.value);
    }
  }

  getVesselsReport(type: string, queryParams: Params): void {
    this._vesselReportActions.actions.getVesselReportPorts({
      type,
      queryParams
    });
  }

  getDetailReportWithCallback(cb?: () => void): void {
    this._portReportActions.actions.getDetailPortReport({ report_id: this.detailPortReport()?.id });
    if (cb) {
      this._portReportActions.actions.getDetailPortReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        cb();
      });
    }
  }
  appendVesselMovements(movements: VesselItinerary[]): void {
    const ids = movements.map((item) => item.id);
    this.vesselMovements.update((prev) => [...prev.filter((item) => !ids.includes(item.id)), ...movements]);
  }
}
